import { useEffect, useRef, useState } from 'react';
import { Play, Square } from 'lucide-react';
import { THOUGHT_STREAM, PROJECTS } from '../data/studio';
import { Reveal, Eyebrow } from './Reveal';

function useAmbientDrone() {
  const ctxRef = useRef(null);
  const nodesRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const start = () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(ctx.destination);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 420;
    filter.connect(gain);
    const oscs = [73.42, 110, 146.83].map((f, i) => {
      const o = ctx.createOscillator();
      o.type = i === 2 ? 'triangle' : 'sine';
      o.frequency.value = f;
      o.detune.value = i * 4 - 4;
      o.connect(filter);
      o.start();
      return o;
    });
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.07;
    lfoGain.gain.value = 180;
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);
    lfo.start();
    gain.gain.linearRampToValueAtTime(0.06, ctx.currentTime + 3);
    ctxRef.current = ctx;
    nodesRef.current = { oscs, lfo, gain };
    setPlaying(true);
  };

  const stop = () => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    nodesRef.current.gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 1.2);
    setTimeout(() => ctx.close(), 1400);
    ctxRef.current = null;
    setPlaying(false);
  };

  useEffect(() => () => { ctxRef.current?.close(); }, []);
  return { playing, toggle: () => (playing ? stop() : start()) };
}

export default function MindStream() {
  const { playing, toggle } = useAmbientDrone();

  return (
    <section id="mind" className="relative py-28 lg:py-40" data-testid="mind-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <Reveal>
              <Eyebrow accent="#E11D48">Kyle's Mind · Live Stream</Eyebrow>
              <h2 className="font-celestial text-3xl sm:text-4xl lg:text-5xl font-light text-slate-100 leading-tight">
                The raw <span className="text-rose-400">nervous system</span> of the studio
              </h2>
              <p className="mt-5 text-sm sm:text-base text-slate-400 leading-relaxed max-w-lg">
                Notes, synth experiments and half-formed hypotheses — published before they become books, apps or papers.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-10 rounded-2xl border border-white/[0.07] bg-gradient-to-b from-slate-900/70 to-slate-950/90 p-7" data-testid="ambient-player">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-rose-400/90">Ambient session 014</p>
                    <p className="text-sm text-slate-300 mt-1.5">Deep-sea pressure drone · D minor</p>
                  </div>
                  <button
                    onClick={toggle}
                    className={`w-12 h-12 rounded-full flex items-center justify-center border transition-all duration-300 ${
                      playing
                        ? 'border-rose-400/70 bg-rose-400/15 text-rose-300 shadow-[0_0_25px_rgba(225,29,72,0.35)]'
                        : 'border-white/15 text-slate-300 hover:border-rose-400/50 hover:text-rose-300'
                    }`}
                    data-testid="audio-player-toggle-button"
                    aria-label={playing ? 'Stop ambient drone' : 'Play ambient drone'}
                  >
                    {playing ? <Square size={16} /> : <Play size={16} className="ml-0.5" />}
                  </button>
                </div>
                <div className="mt-5 flex items-end gap-1 h-8">
                  {Array.from({ length: 32 }).map((_, i) => (
                    <span
                      key={`bar-${i}`}
                      className={`flex-1 rounded-sm bg-rose-400/60 ${playing ? 'eq-bar' : 'scale-y-[0.15]'} origin-bottom transition-transform`}
                      style={{ animationDelay: `${(i % 8) * 0.11}s`, animationDuration: `${0.7 + (i % 5) * 0.13}s` }}
                    />
                  ))}
                </div>
              </div>
            </Reveal>

            <div className="mt-10 space-y-6">
              {PROJECTS.map((p, i) => (
                <Reveal key={p.id} delay={0.1 + i * 0.08}>
                  <div data-testid={`project-progress-${p.id}`}>
                    <div className="flex items-baseline justify-between mb-2">
                      <p className="text-sm text-slate-200">{p.name}</p>
                      <p className="font-mono-precision text-[10px] tracking-[0.2em] text-slate-500">{p.eta} · {p.progress}%</p>
                    </div>
                    <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
                      <Reveal y={0}>
                        <div
                          className="h-full rounded-full transition-all duration-1000"
                          style={{ width: `${p.progress}%`, background: `linear-gradient(90deg, ${p.accent}88, ${p.accent})`, boxShadow: `0 0 10px ${p.accent}66` }}
                        />
                      </Reveal>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-rose-400/40 via-cyan-400/20 to-transparent" />
            <div className="space-y-8">
              {THOUGHT_STREAM.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.1}>
                  <div className="relative pl-10" data-testid={`thought-item-${t.id}`}>
                    <span
                      className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 bg-[#02040a]"
                      style={{ borderColor: t.accent, boxShadow: `0 0 10px ${t.accent}55` }}
                    />
                    <p className="font-mono-precision text-[10px] uppercase tracking-[0.25em]" style={{ color: t.accent }}>
                      {t.type} · {t.time}
                    </p>
                    <p className="text-sm text-slate-300/90 leading-relaxed mt-2 max-w-md">{t.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
